import { useAuthStore } from "@/stores/useAuthStore"
import { camelCase, snakeCase } from "toptaal-shared"
import {
  MTT_ROLE_PERMISSIONS,
  MTTEntities,
  MTTFunctions,
  MTTPermissions,
  MTTRoles,
} from "toptaal-shared"

/**
 * Controleert of de huidige gebruiker een bepaalde actie mag uitvoeren op een resource.
 *
 * Gebruik deze functie BUITEN React-componenten (bijv. in event handlers, services,
 * of utility-functies) waar geen hooks beschikbaar zijn. De functie haalt de rollen
 * direct uit de Zustand-store via `getState()` en reageert dus NIET op wijzigingen.
 *
 * @example
 * if (can("update", "User")) {
 *   await updateUser(...)
 * }
 */
export const can = (action: MTTPermissions, resource: MTTEntities): boolean => {
  const roles = useAuthStore.getState().user?.roles

  return canFromRoles(roles, action, resource)
}

/**
 * React hook-variant van `can`. Gebruik deze BINNEN React-componenten zodat de
 * component automatisch opnieuw rendert wanneer de rollen van de gebruiker veranderen
 * (bijv. na inloggen, uitloggen of een rolwijziging).
 *
 * Retourneert een functie die je vervolgens kunt aanroepen met (action, resource).
 *
 * @example
 * const can = useCan()
 * return <Button disabled={!can("delete", "Match")}>Verwijderen</Button>
 */
export function useCan() {
  const roles = useAuthStore((s) => s.user?.roles)

  return (action: MTTPermissions, resource: MTTEntities) =>
    canFromRoles(roles, action, resource)
}

/**
 * Interne helper: controleert of een lijst rollen een specifieke rol bevat.
 *
 * Rollen worden eerst gennormaliseerd naar camelCase, zodat varianten als
 * "super_admin", "super-admin" en "superAdmin" allemaal als gelijk worden gezien.
 */
const hasRoleFromRoles = (
  roles: MTTRoles[] | undefined,
  targetRole: MTTRoles,
): boolean => {
  if (!roles || roles.length === 0) return false

  const camelCasedRoles = roles.map((role) => camelCase(role))

  return camelCasedRoles.includes(camelCase(targetRole))
}

/**
 * Controleert of de huidige gebruiker een bepaalde rol heeft.
 *
 * Niet-reactief: leest één keer uit de auth-store. Gebruik deze functie buiten
 * React-componenten of in eenmalige checks (bijv. routing-guards). Voor reactieve
 * UI kun je beter `useAuthStore` rechtstreeks aanspreken.
 *
 * @example
 * if (hasRole("admin")) { ... }
 */
export const hasRole = (role: MTTRoles): boolean => {
  const roles = useAuthStore.getState().user?.roles

  return hasRoleFromRoles(roles, role)
}

/**
 * Controleert of de huidige gebruiker een specifieke "function" toegekend heeft
 * gekregen. Functions zijn fijnmaziger dan rollen en worden in de backend
 * opgeslagen als snake_case strings; daarom wordt de input vertaald via `snakeCase`
 * voordat hij wordt vergeleken met de lijst uit de auth-store.
 *
 * @example
 * if (hasFunction("manageInvoices")) { ... }
 */
export const hasFunction = (targetFunction: MTTFunctions): boolean => {
  const functions = useAuthStore.getState().user?.functions

  return functions
    ? functions.includes(snakeCase(targetFunction) as MTTFunctions)
    : false
}

/**
 * Interne kernfunctie voor permissie-checks. Niet geëxporteerd: gebruik `can` of
 * `useCan` als publieke API.
 *
 * Werkwijze:
 * 1. Normaliseer alle rollen naar camelCase zodat ze matchen met de keys in
 *    `MTT_ROLE_PERMISSIONS`.
 * 2. Itereer over de rollen van de gebruiker. Zodra ÉÉN rol de gevraagde actie
 *    op de resource toestaat, retourneert de functie `true` (rollen worden dus
 *    additief gecombineerd — meer rollen = meer rechten, nooit minder).
 * 3. Geeft `false` terug als er geen rollen zijn, de rol niet in de
 *    permissions-map staat, de resource onbekend is, of de actie ontbreekt.
 */
const canFromRoles = (
  roles: MTTRoles[] | undefined,
  action: MTTPermissions,
  resource: MTTEntities,
): boolean => {
  const camelCasedRoles = roles?.map((role) => camelCase(role))
  if (!camelCasedRoles || camelCasedRoles.length === 0) return false

  return camelCasedRoles.some((role) => {
    const permissions =
      MTT_ROLE_PERMISSIONS[role as keyof typeof MTT_ROLE_PERMISSIONS]
    if (!permissions) return false

    const actions = permissions[resource]
    if (!actions) return false

    return actions.includes(action)
  })
}
